/**
 * Validation + completion over the decision registry.
 *
 * Three levels of "valid":
 *   - DRAFT-VALID ({@link checkSettings} `ok`): every stored value is a legal
 *     value for its decision — the bar for saving to `creations/{id}.settings`
 *     (createCreation / updateGameSettings enforce it).
 *   - BUILD-READY ({@link isBuildReady}): draft-valid AND every VISIBLE
 *     spine-section decision is answered — the bar for requestBuild.
 *   - COMPLETE ({@link completeSettings}): a dense {@link GameSettings} —
 *     registry fallbacks overlaid with the visible answered values. The
 *     prompt compiler and the version-doc snapshot only ever see this form.
 */
import { CONTROL_SCHEMES, SEMANTIC_KEYS, isControlSchemeId, validateKeyBindings, type KeyBindings } from '@djgg/controls';
import { DECISIONS, type AnyDecision } from './registry.js';
import { DECISION_IDS, type DecisionId, type GameSettings, type PartialGameSettings } from './types.js';
import { isSpineDecision, isVisible } from './visibility.js';

/** The one NON-decision settings key (see {@link PartialGameSettings}). */
export const KEY_BINDINGS_KEY = 'keyBindings';

/** Legality of a stored `keyBindings` extra, or null when legal. */
function keyBindingsError(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return 'must be an object of key-code lists';
  }
  const known = new Set<string>(SEMANTIC_KEYS);
  for (const key of Object.keys(value)) {
    if (!known.has(key)) return `unknown signal ${JSON.stringify(key)}`;
  }
  const problems = validateKeyBindings(value as Partial<KeyBindings>);
  return problems.length > 0 ? problems.join('; ') : null;
}

/** Set-shaped value equality: `multi` values are SETS of option ids, so two
 *  arrays with the same members are the same answer regardless of order.
 *  Scalars compare with `===`. Shared by {@link import('./compile.js').diffSettings}
 *  and the functions layer's changed-decision detection — one comparison,
 *  never two. */
export function sameSettingsValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const bs = new Set(b as readonly string[]);
    return (a as readonly string[]).every((x) => bs.has(x));
  }
  // Plain objects (the keyBindings extra) compare structurally — identity
  // comparison would report every resave as a change in the audit log.
  if (
    typeof a === 'object' && a !== null && !Array.isArray(a) &&
    typeof b === 'object' && b !== null && !Array.isArray(b)
  ) {
    const ak = Object.keys(a as Record<string, unknown>);
    const bk = Object.keys(b as Record<string, unknown>);
    if (ak.length !== bk.length) return false;
    return ak.every((k) =>
      sameSettingsValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
    );
  }
  return a === b;
}

/** Legality of one stored value for its decision, or null when legal. */
function valueError(id: DecisionId, value: unknown): string | null {
  const decision: AnyDecision = DECISIONS[id];
  switch (decision.kind) {
    case 'choice': {
      if (typeof value !== 'string') return 'must be an option id string';
      const option = decision.options.find((o) => o.id === value);
      if (!option) return `unknown option ${JSON.stringify(value)}`;
      if (option.locked) return `option ${value} is not available yet`;
      return null;
    }
    case 'multi': {
      if (!Array.isArray(value)) return 'must be an array of option ids';
      if (value.length === 0) return 'must select at least one option';
      const seen = new Set<string>();
      for (const entry of value) {
        if (typeof entry !== 'string') return 'must contain only option id strings';
        const option = decision.options.find((o) => o.id === entry);
        if (!option) return `unknown option ${JSON.stringify(entry)}`;
        if (option.locked) return `option ${entry} is not available yet`;
        if (seen.has(entry)) return `duplicate option ${entry}`;
        seen.add(entry);
      }
      return null;
    }
    case 'palette': {
      if (typeof value !== 'string') return 'must be a preset id string';
      if (!decision.presets.some((p) => p.id === value)) {
        return `unknown palette preset ${JSON.stringify(value)}`;
      }
      return null;
    }
  }
}

/** Result of {@link checkSettings}. */
export interface SettingsCheck {
  /** True when every stored entry is legal (draft-valid). */
  ok: boolean;
  /** Per-entry problems (unknown decision id, illegal/locked value). */
  invalid: { decision: string; reason: string }[];
  /** Visible spine decisions with no answer (empty ⇒ build-ready, given ok). */
  missingSpine: DecisionId[];
}

/**
 * Validate an untrusted value as a stored answers map. Never throws — the
 * callers (handlers, wizard) decide how to surface problems.
 */
export function checkSettings(value: unknown): SettingsCheck {
  const invalid: SettingsCheck['invalid'] = [];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, invalid: [{ decision: '', reason: 'settings must be an object' }], missingSpine: [] };
  }
  const raw = value as Record<string, unknown>;
  const known = new Set<string>(DECISION_IDS);
  for (const key of Object.keys(raw)) {
    if (key === KEY_BINDINGS_KEY) {
      const reason = keyBindingsError(raw[key]);
      if (reason) invalid.push({ decision: key, reason });
      continue;
    }
    if (!known.has(key)) {
      invalid.push({ decision: key, reason: 'unknown decision id' });
      continue;
    }
    const reason = valueError(key as DecisionId, raw[key]);
    if (reason) invalid.push({ decision: key, reason });
  }

  const values = raw as PartialGameSettings;
  // Cross-field: a chosen touch preset must be drivable by the adapter's
  // input coverage (the studio filters the picker; this is the server truth).
  const scheme = values.touchScheme;
  if (scheme !== undefined && scheme !== 'auto' && isControlSchemeId(scheme)) {
    const input = values.inputScheme ?? DECISIONS.inputScheme.fallback;
    if (!CONTROL_SCHEMES[scheme].compatibleWith.includes(input)) {
      invalid.push({
        decision: 'touchScheme',
        reason: `${scheme} needs ${CONTROL_SCHEMES[scheme].compatibleWith.join('/')} controls (this game uses ${input})`,
      });
    }
  }
  const missingSpine: DecisionId[] = [];
  for (const id of DECISION_IDS) {
    if (!isSpineDecision(id)) continue;
    if (!isVisible(id, values)) continue;
    if (values[id] === undefined) missingSpine.push(id);
  }

  return { ok: invalid.length === 0, invalid, missingSpine };
}

/** True when the answers are draft-valid AND every visible spine decision is
 *  answered — the requestBuild gate. */
export function isBuildReady(values: PartialGameSettings): boolean {
  const check = checkSettings(values);
  return check.ok && check.missingSpine.length === 0;
}

/**
 * Sanitize an answers map: drop unknown decision ids and illegal values,
 * keeping everything legal. Used on read (a registry epoch break strands
 * stored values — every pre-v4 answer set drops entirely here) — dropping is
 * LOUD via the returned list so callers can log it.
 */
export function sanitizeSettings(value: unknown): {
  values: PartialGameSettings;
  dropped: string[];
} {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { values: {}, dropped: [] };
  }
  const raw = value as Record<string, unknown>;
  const known = new Set<string>(DECISION_IDS);
  const values: Record<string, unknown> = {};
  const dropped: string[] = [];
  for (const key of Object.keys(raw)) {
    if (key === KEY_BINDINGS_KEY) {
      if (keyBindingsError(raw[key])) dropped.push(key);
      else values[key] = raw[key];
      continue;
    }
    if (!known.has(key)) {
      dropped.push(key);
      continue;
    }
    const reason = valueError(key as DecisionId, raw[key]);
    if (reason) dropped.push(key);
    else values[key] = raw[key];
  }
  return { values: values as PartialGameSettings, dropped };
}

/** Every decision at its registry fallback — the total base the answered
 *  values overlay. Deterministic and total by construction. */
function fallbackSettings(): Record<string, unknown> {
  const base: Record<string, unknown> = {};
  for (const id of DECISION_IDS) {
    base[id] = DECISIONS[id].fallback;
  }
  return base;
}

/**
 * Complete sparse answers into a dense {@link GameSettings}: registry
 * fallbacks overlaid with the answered values, with HIDDEN decisions forced
 * back to their fallback (a stored `botSkill` is inert while `vsBot` is not
 * among the play modes — it revives if the mode returns). Assumes draft-valid
 * input (callers gate with {@link checkSettings} / {@link sanitizeSettings}).
 */
export function completeSettings(values: PartialGameSettings): GameSettings {
  const defaults = fallbackSettings();
  // Decisions only: the keyBindings extra is a live-applied runtime pref and
  // never part of the dense build-time object.
  const merged: Record<string, unknown> = { ...defaults };
  for (const id of DECISION_IDS) {
    if (values[id] !== undefined) merged[id] = values[id];
  }
  for (const id of DECISION_IDS) {
    if (!isVisible(id, merged as PartialGameSettings)) merged[id] = defaults[id];
  }
  return merged as GameSettings;
}
